import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, Loader2 } from "lucide-react";
import {
  useUpdateStockOut,
  useMarkStockOutCartPaid,
  useMarkStockOutBulkPaid,
  getListStockOutQueryKey,
  type StockOut,
  type InventoryItem,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { uploadPaymentProof } from "@/lib/upload";

// Captures the operator's payment-receipt details when settling a
// Pay-Per-Use stock-out:
//   * Payment Mode — hard-locked to UPI; the operator's space only
//     settles these charges via UPI, no Cash / Card / Bank Transfer
//     branches needed (confirmed 2026-06-20).
//   * Transaction ID (stored on stock_out.invoice_reference) — required.
//   * Payment proof — required; uploaded via /api/uploads/payment-proof,
//     the returned URL is saved to stock_out.payment_proof_url.
//
// Cart-aware: a multi-item Pay-Per-Use submission spawns N rows that
// share a cart_id. Submit calls POST /operations/stock-out/cart/:cartId/payment
// so ALL sibling rows are updated atomically with the same proof +
// transaction id. Singleton rows (cartId == null) fall back to a
// PATCH on the row itself.

interface CartTarget {
  id: string;
  cartId: string | null;
  rows: StockOut[];
  totalAmount: number;
}

interface Props {
  cart: CartTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemById: Map<number, InventoryItem>;
}

export function MarkStockOutPaidDialog({ cart, open, onOpenChange, itemById }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateStockOut = useUpdateStockOut();
  const markCartPaid = useMarkStockOutCartPaid();
  const markBulkPaid = useMarkStockOutBulkPaid();
  const [txnRef, setTxnRef] = useState("");
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  function reset() {
    setTxnRef("");
    setProofFile(null);
    setUploading(false);
  }

  async function handleSubmit() {
    if (!cart) return;

    if (!txnRef.trim()) {
      toast({ title: "Transaction ID is required.", variant: "destructive" });
      return;
    }
    if (!proofFile) {
      toast({ title: "Payment proof is required.", variant: "destructive" });
      return;
    }

    try {
      // 1. Upload the proof file first so we have a URL to attach.
      setUploading(true);
      const result = await uploadPaymentProof(proofFile);
      setUploading(false);
      if (!result.ok) {
        toast({
          title: `Upload failed: ${result.message}`,
          variant: "destructive",
        });
        return;
      }
      const proofUrl = result.url;

      // 2. Apply payment to the cart (multi-row) or the single row.
      const payload = {
        paymentStatus: "Paid" as const,
        paymentMethod: "UPI",
        invoiceReference: txnRef.trim(),
        paymentProofUrl: proofUrl,
      };
      // Three paths:
      //   • Cart with a shared cart_id → single POST /cart/:cartId/payment.
      //   • Multi-row retro-bundle (no shared cartId, multiple rows) →
      //     POST /bulk-payment with the row ids (2026-07-15 collect-across-
      //     sales UX). Cascades to each row's linked charge in one txn.
      //   • Singleton pending row → PATCH the row directly.
      if (cart.cartId) {
        await markCartPaid.mutateAsync({ cartId: cart.cartId, data: payload });
      } else if (cart.rows.length > 1) {
        await markBulkPaid.mutateAsync({
          data: { ...payload, stockOutIds: cart.rows.map((r) => r.id) },
        });
      } else {
        await updateStockOut.mutateAsync({ id: cart.rows[0]!.id, data: payload });
      }

      toast({
        title: cart.rows.length > 1
          ? `${cart.rows.length} items marked as paid.`
          : "Marked as paid.",
      });
      queryClient.invalidateQueries({ queryKey: getListStockOutQueryKey() });
      reset();
      onOpenChange(false);
    } catch {
      setUploading(false);
      toast({ title: "Could not mark paid.", variant: "destructive" });
    }
  }

  const busy =
    uploading || updateStockOut.isPending || markCartPaid.isPending || markBulkPaid.isPending;
  const heading =
    cart && cart.rows.length === 1
      ? itemById.get(cart.rows[0]!.itemId)?.name ?? `Item #${cart.rows[0]!.itemId}`
      : cart
      ? `Cart of ${cart.rows.length} items`
      : null;

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Mark as Paid</SheetTitle>
          <SheetDescription>
            {heading
              ? `Recording payment for ${heading}.`
              : "Capture how this charge was settled."}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Payment Mode</Label>
            <p className="text-sm font-medium">UPI</p>
          </div>

          {/* Multi-item cart: show breakdown so the operator knows
              exactly what the one merchant copy is covering. */}
          {cart && cart.rows.length > 1 && (
            <div className="space-y-1 rounded-md border bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">Items in this cart</p>
              <ul className="space-y-0.5 text-sm">
                {cart.rows.map((r) => {
                  const it = itemById.get(r.itemId);
                  return (
                    <li
                      key={`mp-line-${r.id}`}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {it?.name ?? `Item #${r.itemId}`}{" "}
                        <span className="text-xs text-muted-foreground">
                          × {Number(r.quantityIssued)}
                        </span>
                      </span>
                      <span className="font-mono tabular-nums shrink-0">
                        ₹{Number(r.amountCharged ?? 0).toLocaleString("en-IN")}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="pt-2 mt-1 border-t flex items-center justify-between text-sm font-medium">
                <span>Total</span>
                <span className="font-mono tabular-nums">
                  ₹{cart.totalAmount.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              Transaction ID <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="e.g. 6291XXXX1234 (UPI ref)"
              value={txnRef}
              onChange={(e) => setTxnRef(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>
              Payment Proof (Merchant Copy) <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setProofFile(e.target.files?.[0] ?? null)}
                className="text-xs"
              />
              {uploading && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            {proofFile && (
              <p className="text-xs text-muted-foreground truncate">
                <Upload className="inline h-3 w-3 mr-1" />
                {proofFile.name} ({Math.round(proofFile.size / 1024)} KB)
              </p>
            )}
          </div>
        </div>

        <div className="mt-auto pt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={busy}>
            {busy ? "Saving…" : "Mark Paid"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
